import React, { useState, useRef, useContext, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import ModalHeading from '../../components/shared/modal-heading';
import { AccountsContext } from '../../components/context/account-context';
import { prependDefault } from '../../utils/structure-options-utils';
import { uploadMedia } from '../../services/upload-service';
import AttachmentInput from '../../components/shared/attachment-input';
import { getServicesMedia, updateService } from '../../services/services-service';
import MultipleAttachmentInput from '../../components/shared/multiple-attachemnt';
import Loader from '../../components/shared/loader';

const EditModal = ({ selectedRow, setSelectedRow, globalDataloading, setIsEditModalOpen, setData, isVehicleTab = false, vendors, vehicles, types }) => {
    const { accountId, attributes } = useContext(AccountsContext);
    const [imagePreview, setImagePreview] = useState(selectedRow?.expense?.media?.signedThumbUrl || null);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [loadingFields, setLoadingFields] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [isMultiUploading, setIsMultiUploading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [fetchingMedia, setFetchingMedia] = useState(false);
    const fileInputRef = useRef(null);
    const [error, setError] = useState();

    // Get the serivces media data
    const fetchServicesMedia = async () => {
        try {
            setFetchingMedia(true);
            const res = await getServicesMedia(accountId, selectedRow.serviceId);
            if (res.success) {
                const mediaFiles = res.data?.data || [];
                setUploadedFiles(mediaFiles.map(file => ({
                    mediaId: file.mediaId,
                    fileType: file.fileType,
                    fileName: file.fileName,
                    signedUrl: file.signedUrl,
                    signedThumbUrl: file.signedThumbUrl,
                    imagePreview: file.fileType?.startsWith('image/') ? file.signedThumbUrl || file.signedUrl : null
                })));
                setError(null);
            } else {
                setError(res.data?.message || 'Failed to fetch media');
            }
        } catch (error) {
            console.error('Error fetching media:', error);
            setError('An error occurred while fetching media');
        } finally {
            setFetchingMedia(false);
        }
    };

    useEffect(() => {
        fetchServicesMedia();
    }, [accountId, selectedRow.serviceId]);


    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (uploadStatus.message && uploadStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUploadStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [uploadStatus]);

    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    // Handle multi file upload
    const handleFileUpload = async (files) => {
        if (files.length === 0) return;

        setIsMultiUploading(true);
        setUploadStatus({ message: '', type: '' });

        try {
            // Upload files to the server
            const uploadPromises = files.map((file) =>
                uploadMedia(accountId, file, 'service', selectedRow.serviceId)
            );
            const uploadResponses = await Promise.all(uploadPromises);

            console.log("Multi respinse : ", uploadResponses)

            // Process responses
            const successfulUploads = uploadResponses.filter(res => res.success);
            const failedUploads = uploadResponses.filter(res => !res.success);
            console.log(successfulUploads)

            if (failedUploads.length > 0) {
                setUploadStatus({
                    message: `${failedUploads.length} file(s) failed to upload`,
                    type: 'error',
                });
            }

            if (successfulUploads.length > 0) {
                const newFiles = successfulUploads.map(res => ({
                    mediaId: res.data.data.mediaId,
                    fileType: res.data.data.fileType,
                    fileName: res.data.data.fileName,
                    signedUrl: res.data.data.signedUrl,
                    signedThumbUrl: res.data.data.signedThumbUrl,
                    imagePreview: res.data.data.fileType?.startsWith('image/') ? res.data.data.signedThumbUrl || res.data.data.signedUrl : null
                }));

                setUploadedFiles(prev => [...prev, ...newFiles]);
                setUploadStatus({
                    message: `${successfulUploads.length} file(s) uploaded successfully!`,
                    type: 'success',
                });
            }
        } catch (error) {
            console.error('Error uploading media:', error);
            setUploadStatus({
                message: 'Failed to upload files',
                type: 'error',
            });
        } finally {
            setIsMultiUploading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsUploading(true);
            const reader = new FileReader();
            reader.onloadend = async () => {
                setImagePreview(reader.result);

                try {
                    const response = await uploadMedia(accountId, file, 'expense', selectedRow.expense.expenseId);
                    console.log("single respinse : ", response)

                    if (response.success) {
                        const updatedRow = {
                            ...selectedRow,
                            expense: {
                                ...selectedRow.expense,
                                media: response.data
                            }
                        };
                        setSelectedRow(updatedRow);
                        setData(prevData => prevData.map(row =>
                            row.serviceId === selectedRow.serviceId ? updatedRow : row
                        ));
                        setUploadStatus({ message: 'Receipt uploaded successfully!', type: 'success' });
                    } else {
                        setImagePreview(null);
                        setUploadStatus({
                            message: response.data.message || 'Failed to upload receipt',
                            type: 'error'
                        });
                    }
                } finally {
                    setIsUploading(false);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle multi file removal
    const handleRemoveFile = async (fileToRemove) => {
        try {
            setUploadedFiles(prev => prev.filter(file => file.mediaId !== fileToRemove.mediaId));
        } catch (error) {
            console.error('Error removing file:', error);
            // Add it back to the list if deletion failed
            setUploadedFiles(prev => [...prev, fileToRemove]);
            setUploadStatus({
                message: 'Failed to delete file',
                type: 'error',
            });
        }
    };

    const handleRemoveImage = () => {
        if (isUploading) return;
        const updatedRow = {
            ...selectedRow,
            expense: {
                ...selectedRow.expense,
                media: null
            }
        };
        setImagePreview(null);
        setSelectedRow(updatedRow);
        setData(prevData => prevData.map(row =>
            row.serviceId === selectedRow.serviceId ? updatedRow : row
        ));
        setUploadStatus({ message: '', type: '' });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFieldUpdate = async (value, fieldName) => {
        console.log(value, fieldName)
        setLoadingFields(prev => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            serviceTime: 'serviceTime',
            vendor: 'vendorId',
            vehicle: 'vehicleId',
            odometer: 'odometer',
            amount: 'amount',
            types: 'types',
            status: 'status',
            note: 'note'
        };

        const payload = {};
        if (fieldName === 'serviceTime') {
            payload[fieldMap[fieldName]] = new Date(value).toISOString();
        } else if (fieldName === 'vendor') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'vehicle') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'amount') {
            payload[fieldMap[fieldName]] = parseFloat(value);
        } else if (fieldName === 'odometer') {
            payload[fieldMap[fieldName]] = parseInt(value);
        } else {
            payload[fieldMap[fieldName]] = value;
        }

        try {
            const response = await updateService(accountId, selectedRow.serviceId, payload);
            console.log("Update the services  : ", response)
            if (response.success) {
                const updatedRow = { ...selectedRow };
                switch (fieldName) {
                    case 'serviceTime':
                        updatedRow.serviceTime = payload.serviceTime;
                        break;
                    case 'vendor':
                        updatedRow.vendor = {
                            vendorId: value,
                            name: vendorOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'vehicle':
                        updatedRow.vehicle = {
                            vehicleId: value,
                            name: vehicleOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;

                    case 'amount':
                        updatedRow.expense = { ...updatedRow.expense, amount: parseFloat(value) };
                        break;
                    default:
                        updatedRow[fieldName] = value;
                }
                setSelectedRow(updatedRow);
                setData(prevData => prevData.map(row =>
                    row.serviceId === selectedRow.serviceId ? updatedRow : row
                ));
                setUpdateStatus({ message: `${labelMap[fieldName] || fieldName} updated successfully!`, type: 'success' });
            } else {
                console.log(response.data)
                setUpdateStatus({
                    message: response.data.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
                // throw new Error('API update failed');
            }
        } catch (error) {
            setUpdateStatus({
                message: error.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
            // throw error;
        } finally {
            setLoadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    // console.log(updateStatus)

    const labelMap = {
        serviceTime: 'Service Time',
        vendor: 'Vendor',
        vehicle: 'Vehicle',
        odometer: 'Odometer',
        amount: 'Amount',
        types: 'Types',
        status: 'Status',
        note: 'Note'
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const vendorOptions = prependDefault(vendors.map(v => ({ value: v.vendorId, label: v.label })), 'Vendor');
    const vehicleOptions = prependDefault(vehicles.map(v => ({ value: v.vehicleId, label: v.label })), 'Vehicle');
    const typeOptions = prependDefault(types.map(v => ({ value: v.slug, label: v.label })), 'Types');


    return (
        <div className="space-y-3 px-4 pt-3 relative">
            <ModalHeading className="text-left mb-3">Service Detail Information</ModalHeading>

            <button
                className='hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full'
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className='text-xl' />
            </button>

            {(uploadStatus.message || updateStatus.message) && (
                <div className={`px-4 py-2 text-sm ${(uploadStatus.type || updateStatus.type) === 'success' ? 'text-green-600 bg-green-100' :
                    (uploadStatus.type || updateStatus.type) === 'error' ? 'text-red-600 bg-red-100' :
                        'text-blue-600 bg-blue-100'
                    } rounded-md mx-4`}>
                    {uploadStatus.message || updateStatus.message}
                </div>
            )}

            {error && (
                <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                    {error}
                </div>
            )}

            {(fetchingMedia || globalDataloading) ? (
                <Loader />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <EditableField
                        label="Service Time"
                        name="serviceTime"
                        value={selectedRow?.serviceTime || ''}
                        onChange={handleFieldUpdate}
                        type="datetime-local"
                        isLoading={loadingFields.serviceTime}
                    />

                    <EditableField
                        label="Vendor"
                        name="vendor"
                        type='select'
                        options={vendorOptions}
                        value={selectedRow?.vendor?.vendorId || ''}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.vendor}
                    />

                    <EditableField
                        label="Vehicle"
                        name="vehicle"
                        type='select'
                        value={selectedRow?.vehicle?.vehicleId || ''}
                        options={vehicleOptions}
                        onChange={handleFieldUpdate}
                        isEditable={!isVehicleTab}
                        isLoading={loadingFields.vehicle}
                    />

                    <EditableField
                        label="Odometer"
                        name="odometer"
                        value={selectedRow?.odometer || ''}
                        onChange={handleFieldUpdate}
                        type="number"
                        isLoading={loadingFields.odometer}
                        unit={attributes?.distanceUnit}
                    />

                    <EditableField
                        label="Amount"
                        name="amount"
                        value={selectedRow?.expense?.amount || ''}
                        onChange={handleFieldUpdate}
                        type="number"
                        isEditable={false}
                        isLoading={loadingFields.amount}
                        unit={attributes?.currency}
                    />

                    <div className='sm:col-span-2 col-span-1'>
                        <EditableField
                            label="Types"
                            name="types"
                            type="select"
                            value={selectedRow?.types || ''}
                            onChange={handleFieldUpdate}
                            isMulti={true}
                            options={typeOptions}
                            isLoading={loadingFields.types}
                        />
                    </div>

                    <div>
                        <EditableField
                            label="Status"
                            name="status"
                            value={selectedRow?.status || 'submitted'}
                            onChange={handleFieldUpdate}
                            type="select"
                            isEditable={false}
                            isLoading={loadingFields.status}
                        />
                    </div>


                    <EditableField
                        label="Note"
                        name="note"
                        type='textarea'
                        rows={2}
                        value={selectedRow?.note || ''}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.note}
                    />

                    <AttachmentInput
                        fileInputRef={fileInputRef}
                        handleImageUpload={handleImageUpload}
                        triggerFileInput={triggerFileInput}
                        imagePreview={imagePreview}
                        selectedRow={selectedRow}
                        handleRemoveImage={handleRemoveImage}
                        isUploading={isUploading}
                        accountId={accountId}
                    />

                    <MultipleAttachmentInput
                        label={'Attach Services Media'}
                        handleFileUpload={handleFileUpload}
                        uploadedFiles={uploadedFiles}
                        handleRemoveFile={handleRemoveFile}
                        isUploading={isMultiUploading}
                        accountId={accountId}
                        fileInputRef={fileInputRef}
                    />
                </div>
            )}
        </div>
    );
};

export default EditModal;